"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowUpRight, Plus } from "lucide-react";

interface ProductCategoryProps {
  data: {
    id: number;
    title?: string;
    description?: string;
    isLive: boolean;
    useH1Tag: boolean;
    Card: {
      id: number;
      title?: string;
      description?: string;
      href?: string;
      screenDisplay: boolean;
      tag?: string;
      image?: {
        data?: {
          attributes?: {
            url?: string;
            alternativeText?: string | null;
          };
        };
      };
    }[];
  };
}

const Layout2 = ({ data }: ProductCategoryProps) => {
  if (!data?.isLive) return null;

  const titleParts = data?.title?.split(" ") || [];
  const firstPart = titleParts[0] || "";
  const restPart = titleParts.slice(1).join(" ") || "";

  const displayCards =
    data?.Card?.filter((c) => c?.screenDisplay)?.slice(0, 4) || [];

  return (
    <section className="py-24 bg-white px-6 md:px-12 border-t border-neutral-100 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="w-8 h-[2px] bg-[var(--secondary)]" />
              {data?.description && (
                <p className="text-[10px] font-black uppercase tracking-[0.4em] text-gray-700">
                  {data.description}
                </p>
              )}
            </div>

            <div className="text-4xl md:text-6xl font-black text-neutral-900 uppercase italic tracking-tighter leading-none">
              {data?.useH1Tag ? (
                <h1>
                  {firstPart}{" "}
                  <span style={{ color: "var(--secondary)" }}>
                    {restPart}
                  </span>
                </h1>
              ) : (
                <h2>
                  {firstPart}{" "}
                  <span style={{ color: "var(--secondary)" }}>
                    {restPart}
                  </span>
                </h2>
              )}
            </div>
          </div>

          <Link
            href="/collections"
            className="group flex items-center gap-3 text-[11px] font-black uppercase tracking-[0.2em] text-neutral-900 border-b-2 border-neutral-900 pb-1 transition-colors hover:text-[var(--secondary)] hover:border-[var(--secondary)]"
          >
            Browse All
            <ArrowUpRight
              size={14}
              className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </Link>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
          {displayCards.map((card, index) => {
            const imageUrl = card?.image?.data?.attributes?.url;
            const altText =
              card?.image?.data?.attributes?.alternativeText ||
              card?.title ||
              "Product";

            return (
              <div
                key={card?.id || index}
                className="group flex flex-col cursor-pointer isolate"
              >
                {/* Image */}
                <div className="relative aspect-[3/4] overflow-hidden bg-neutral-50 border border-neutral-100">
                  {imageUrl ? (
                    <Image
                      src={imageUrl}
                      alt={altText}
                      fill
                      quality={80}
                      sizes="(max-width: 768px) 50vw, 25vw"
                      className="object-cover grayscale-[15%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-neutral-100 text-gray-700 text-[10px] uppercase font-bold">
                      Image Coming Soon
                    </div>
                  )}

                  {card?.tag && (
                    <div className="absolute top-0 left-0 z-10">
                      <span className="bg-[var(--secondary)] text-white text-[9px] font-black uppercase tracking-widest px-3 py-1.5 italic">
                        {card.tag}
                      </span>
                    </div>
                  )}

                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300 z-20">
                    <Link
                      href={card?.href || "#"}
                      className="w-full bg-white text-neutral-900 border border-neutral-900 py-4 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 hover:bg-neutral-900 hover:text-white transition-all shadow-xl"
                    >
                      <Plus size={14} /> Shop Category
                    </Link>
                  </div>
                </div>

                {/* Typography */}
                <div className="mt-6 flex flex-col space-y-1">
                  <div className="flex justify-between items-baseline gap-2">
                    <h3 className="text-[15px] font-black text-neutral-900 uppercase tracking-tight transition-colors group-hover:text-[var(--secondary)] line-clamp-1">
                      {card?.title}
                    </h3>
                    {card?.description && (
                      <span className="text-[var(--secondary)] font-black text-sm italic tracking-tighter whitespace-nowrap">
                        {card.description}
                      </span>
                    )}
                  </div>

                  <div
                    className="h-[1.5px] w-0 group-hover:w-full transition-all duration-300 mt-2"
                    style={{ backgroundColor: "var(--secondary)" }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Layout2;
